// src/app/sidebar/sidebar.component.ts
import { Component, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/internal/operators/filter';
import { NotificationService } from '../notifications/notification.service';

interface NavItem {
  name: string;
  icon: string;
  link: string;
  active: boolean;
  module?: string; // Maps to modules object key
  superUserOnly?: boolean; // If true, only shown to SuperUser role
}

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.css']
})
export class SidebarComponent implements OnInit {
  navItems: NavItem[] = [
    { name: 'Approval', icon: 'shield-fill-check', link: '/admin/marketing-offers/approval', active: false, module: 'marketing_offers', superUserOnly: true },
    { name: 'Members', icon: 'person-plus-fill', link: '/groups/view-member', active: false, module: 'members' },
    { name: 'Marketing Offers', icon: 'megaphone-fill', link: '/admin/marketing-offers', active: false, module: 'marketing_offers' },
    { name: 'SEO Pages', icon: 'globe2', link: '/admin/seo-pages', active: false, module: 'seo_pages' },
    { name: 'Appointments', icon: 'calendar-check-fill', link: '/admin/appointments', active: false, module: 'appointments' },
    { name: 'LexiZ Lawyers', icon: 'briefcase-fill', link: '/admin/lexiz-lawyers', active: false, module: 'lawyers' },
    { name: 'Lawyer Leads', icon: 'person-badge-fill', link: '/admin/lawyer-leads', active: false, module: 'contact_us' },
    { name: 'LexiZ Clients', icon: 'people-fill', link: '/admin/lexiz-clients', active: false, module: 'clients' },
    { name: 'Client Leads', icon: 'person-lines-fill', link: '/admin/client-leads', active: false, module: 'contact_us' },
    { name: 'LexiZ Corporate', icon: 'building-fill', link: '/admin/lexiz-corporate', active: false, module: 'corporate' },
    { name: 'Other Products', icon: 'box-fill', link: '/admin/other-products', active: false, module: 'other_products' },
    { name: 'Contact Us', icon: 'envelope-fill', link: '/admin/contact-us', active: false, module: 'contact_us' },
    { name: 'Audit Trails', icon: 'clipboard2-data-fill', link: '/audit-trails', active: false, module: 'audit' },
    { name: 'Notifications', icon: 'bell-fill', link: '/notifications', active: false, module: 'notification' }
  ];

  role: any;
  modules: any = {};

  get unreadCount(): number {
    return this.notifService.unreadCount;
  }

  constructor(
    private router: Router,
    private notifService: NotificationService
  ) {}

  ngOnInit() {
    this.role = localStorage.getItem('role');
    const groups = localStorage.getItem('groups') || '';
    
    // Load modules configuration from localStorage
    const modulesStr = localStorage.getItem('modules');
    if (modulesStr) {
      try {
        this.modules = JSON.parse(modulesStr);
      } catch (e) {
        console.error('Error parsing modules config', e);
        this.modules = {};
      }
    }

    // Filter nav items based on role and modules
    this.filterNavItemsByRole(groups);

    this.updateActiveState(this.router.url);

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.updateActiveState(this.router.url);
    });
  }

  /**
   * Filter navigation items based on user role and module permissions.
   * SuperUser-only items are filtered out for non-SuperUser roles.
   */
  filterNavItemsByRole(groups: string = ''): void {
    const isSuperUser = groups === 'SuperUser';

    if (this.role === 'AAM') {
      // AAM (Account Access Manager) - only see Members; also strip superUserOnly items
      this.navItems = this.navItems.filter(item => item.name === 'Members');
    } else if (this.role === 'TM' || this.role === 'GH') {
      // TM/GH - exclude Members, filter by modules, strip superUserOnly items
      this.navItems = this.navItems.filter(item => {
        if (item.name === 'Members') return false;
        if (item.superUserOnly && !isSuperUser) return false;
        return !item.module || this.modules[item.module] !== false;
      });
    } else {
      // Default / SuperUser — filter by module permissions; strip superUserOnly if not SuperUser
      this.navItems = this.navItems.filter(item => {
        if (item.superUserOnly && !isSuperUser) return false;
        return !item.module || this.modules[item.module] !== false;
      });
    }

    // Navigate user to appropriate page if current role doesn't have access to current path
    if (this.role === 'AAM') {
      this.router.navigate(['groups/view-member']);
    } else if (this.role === 'TM' || this.role === 'GH') {
      this.router.navigate(['admin/marketing-offers']);
    }
  }

  updateActiveState(url: string) {
   
    const isFromApproval = url.includes('fromApproval=true');
    const isFromDeleted  = url.includes('fromDeleted=true');
 
    const path = url.split('?')[0];
 
    this.navItems.forEach(item => {
      // Reset first
      item.active = false;
 
      if ((path === '/viewdoc' || path.startsWith('/viewdoc/')) && item.link === '/queries') {
        item.active = true;
      } else if ((path === '/download-details' || path.startsWith('/download-details/')) && item.link === '/queries') {
        item.active = true;
      } else if ((path === '/review-details' || path.startsWith('/review-details/')) && item.link === '/queries') {
        item.active = true;
      } else if (path.startsWith('/groups/view-group') && item.link === '/groups/view-member') {
        item.active = true;
      } else if (path.startsWith('/groups/view-member') && item.link === '/groups/view-member') {
        item.active = true;
      } else if (path.startsWith('/groups/create-member') && item.link === '/groups/view-member') {
        item.active = true;
      } else if (path.startsWith('/groups/create-group') && item.link === '/groups/view-member') {
        item.active = true;
      } else if (path.startsWith('/document/upload/firm') && item.link === '/document/view/firm') {
        item.active = true;
      } else if (path.startsWith('/document/view/firm') && item.link === '/document/view/firm') {
        item.active = true;
      } else if (path.startsWith('/document/view/delete') && item.link === '/document/view/firm') {
        item.active = true;
 
      } else if (path === '/admin/marketing-offers/approval' && item.link === '/admin/marketing-offers/approval') {
        item.active = true;
 
      }
      else if (path === '/admin/client-leads' && item.link === '/admin/client-leads') {
        item.active = true;
      }
      else if (path === '/admin/lawyer-leads' && item.link === '/admin/lawyer-leads') {
        item.active = true;
      }
      else if (path === '/admin/contact-us' && item.link === '/admin/contact-us') {
        item.active = true;
      }
      else if (
        (isFromApproval || isFromDeleted) &&
        (path.startsWith('/admin/marketing-offers/view/') || path.startsWith('/admin/marketing-offers/edit/')) &&
        item.link === '/admin/marketing-offers/approval'
      ) {
        item.active = true;
      } else if (
        path.startsWith('/admin/marketing-offers') &&
        path !== '/admin/marketing-offers/approval' &&
        !(isFromApproval || isFromDeleted) &&
        item.link === '/admin/marketing-offers'
      ) {
        item.active = true;
 
      } else if (path.startsWith('/admin/members') && item.link === '/admin/members') {
        item.active = true;
      } else if (path.startsWith('/admin/lexiz-lawyers') && item.link === '/admin/lexiz-lawyers') {
        item.active = true;
      } else if (path.startsWith('/admin/lexiz-clients') && item.link === '/admin/lexiz-clients') {
        item.active = true;
      } else if (path.startsWith('/admin/lexiz-corporate') && item.link === '/admin/lexiz-corporate') {
        item.active = true;
      } else if (path.startsWith('/admin/other-products') && item.link === '/admin/other-products') {
        item.active = true;
      } else if (path.startsWith('/admin/appointments') && item.link === '/admin/appointments') {
        item.active = true;
      } else if (path.startsWith('/admin/seo-pages') && item.link === '/admin/seo-pages') {
        item.active = true;
      } else if (path.startsWith('/notifications') && item.link === '/notifications') {
        item.active = true;
      } else if (path.startsWith('/audit-trails') && item.link === '/audit-trails') {
        item.active = true;
      }
    });
  }
 
  toggleActive(index: number): void {
    this.navItems.forEach((item, i) => {
      item.active = i === index;
    });
  }
}